// shell/BottomTabBar/BottomTabBar.tsx — fixed mobile bottom navigation.
//
// Signed in — 5 slots: Home · Explore · ⊕ Compose (center FAB) · Messages · Profile.
// Signed out (Growth slice, 1.21.60) — 3 slots: Home · Explore · Sign in. Compose,
// Messages, and Profile are all signed-in-only concepts (posting, DMs, an own profile
// that doesn't exist yet) — a guest gets a "Sign in" tab instead, carrying ?next= back
// to wherever they were, matching useRequireAuth's redirect convention everywhere else.
//
// • NavLink active state (Home uses `end` to avoid matching every path).
// • Messages badge = dm + group unread, same hooks as Rail.
// • ≥44px touch targets; padding-bottom: env(safe-area-inset-bottom).
// • role="navigation" aria-label; focus-visible rings.
// • Compose slot fires onCompose() callback — NOT a navigation link.

import { NavLink, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Icon } from '@/icons';
import { useMessagesByType, useSession } from '@/hooks';
import { CountBadge } from '../CountBadge';

export interface BottomTabBarProps {
  onCompose: () => void;
}

// ── Styled pieces ─────────────────────────────────────────────────────────────

const Nav = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 50;
  display: flex;
  align-items: stretch;
  height: calc(56px + env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
  background: ${({ theme }) => theme.color.surfaceChrome};
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const tabBase = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  gap: 3px;
  text-decoration: none;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};

  /* focus-visible ring */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brandFrom};
    outline-offset: -2px;
    border-radius: ${({ theme }) => theme.radius.sm};
  }

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const Tab = styled(NavLink)`
  ${tabBase}

  &.active {
    color: ${({ theme }) => theme.color.brandFrom};
  }
`;

const ComposeButton = styled.button`
  ${tabBase}
  flex: 1;
  position: relative;
`;

const FabCircle = styled.span`
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.accentCyan};
  color: ${({ theme }) => theme.color.onAccent};
`;

const BadgeWrap = styled.div`
  position: relative;
  display: inline-flex;

  /* Position the CountBadge as an overlay on the icon */
  & > span:last-child:not(:first-child) {
    position: absolute;
    top: -4px;
    right: -6px;
    pointer-events: none;
  }
`;

const TabLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1;
`;

// ── Component ─────────────────────────────────────────────────────────────────

export function BottomTabBar({ onCompose }: BottomTabBarProps) {
  const { isAuthenticated } = useSession();
  const location = useLocation();
  const dmUnread = useMessagesByType('dm').conversations.reduce((s, c) => s + c.unread, 0);
  const groupUnread = useMessagesByType('group').conversations.reduce((s, c) => s + c.unread, 0);
  const msgUnread = dmUnread + groupUnread;
  const next = encodeURIComponent(location.pathname + location.search);

  return (
    <Nav role="navigation" aria-label="Mobile navigation" data-testid="bottom-tab-bar">
      <Tab to="/" end aria-label="Home">
        <Icon name="home" size={22} />
        <TabLabel>Home</TabLabel>
      </Tab>

      <Tab to="/explore" aria-label="Explore">
        <Icon name="compass" size={22} />
        <TabLabel>Explore</TabLabel>
      </Tab>

      {isAuthenticated ? (
        <>
          <ComposeButton
            type="button"
            aria-label="Compose"
            onClick={onCompose}
            data-testid="compose-fab"
          >
            <FabCircle aria-hidden="true">
              <Icon name="plus" size={22} />
            </FabCircle>
          </ComposeButton>

          <Tab to="/messages" aria-label="Messages">
            <BadgeWrap>
              <Icon name="mail" size={22} />
              {msgUnread > 0 && (
                <CountBadge data-testid="messages-badge">{msgUnread}</CountBadge>
              )}
            </BadgeWrap>
            <TabLabel>Messages</TabLabel>
          </Tab>

          <Tab to="/profile" aria-label="Profile">
            <Icon name="user" size={22} />
            <TabLabel>Profile</TabLabel>
          </Tab>
        </>
      ) : (
        <Tab to={`/signin?next=${next}`} aria-label="Sign in">
          <Icon name="user" size={22} />
          <TabLabel>Sign in</TabLabel>
        </Tab>
      )}
    </Nav>
  );
}
